/**
 * Utility functions for YouTube video integration in TinyMCE editor
 * Handles URL parsing and thumbnail generation for email-compatible YouTube embeds
 */

export interface IYouTubeVideoInfo {
	videoId: string;
	thumbnailUrl: string;
	originalUrl: string;
	title?: string;
}

/**
 * Escape HTML entities to prevent XSS attacks
 */
function escapeHtml(text: string): string {
	const map: Record<string, string> = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#39;',
	};
	return text.replace(/[&<>"']/g, (m: string) => map[m]);
}

/**
 * Validate that a URL is a proper YouTube URL using URL constructor
 */
function isValidYouTubeUrl(url: string): boolean {
	try {
		const urlObj = new URL(url);
		const validHosts = ['youtube.com', 'www.youtube.com', 'youtu.be', 'm.youtube.com'];
		return validHosts.includes(urlObj.hostname.toLowerCase());
	} catch {
		return false;
	}
}

export const YouTubeUtils = {
	/**
	 * Extract YouTube video ID from various URL formats with proper validation
	 * Supports: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID, m.youtube.com/watch?v=ID
	 */
	extractVideoId(url: string): string | null {
		if (!url || typeof url !== 'string') return null;

		// First validate it's a proper YouTube URL
		if (!isValidYouTubeUrl(url)) return null;

		try {
			const urlObj = new URL(url);

			// Extract video ID based on hostname and path
			if (urlObj.hostname === 'youtu.be') {
				// Handle youtu.be/VIDEO_ID format
				const videoId = urlObj.pathname.slice(1);
				return this.isValidVideoId(videoId) ? videoId : null;
			} else if (urlObj.hostname.includes('youtube.com')) {
				// Handle youtube.com formats
				if (urlObj.pathname === '/watch') {
					// youtube.com/watch?v=VIDEO_ID
					const videoId = urlObj.searchParams.get('v');
					return videoId && this.isValidVideoId(videoId) ? videoId : null;
				} else if (urlObj.pathname.startsWith('/embed/')) {
					// youtube.com/embed/VIDEO_ID
					const videoId = urlObj.pathname.split('/embed/')[1]?.split('/')[0];
					return videoId && this.isValidVideoId(videoId) ? videoId : null;
				} else if (urlObj.pathname.startsWith('/shorts/')) {
					// youtube.com/shorts/VIDEO_ID
					const videoId = urlObj.pathname.split('/shorts/')[1]?.split('/')[0]?.split('?')[0];
					return videoId && this.isValidVideoId(videoId) ? videoId : null;
				}
			}
		} catch {
			return null;
		}

		return null;
	},

	/**
	 * Validate YouTube video ID format (11 characters, alphanumeric, dash, underscore)
	 */
	isValidVideoId(videoId: string): boolean {
		if (!videoId || typeof videoId !== 'string') return false;
		return /^[a-zA-Z0-9_-]{11}$/.test(videoId);
	},

	/**
	 * Check if a URL is a valid YouTube URL
	 */
	isYouTubeUrl(url: string): boolean {
		return this.extractVideoId(url) !== null;
	},

	/**
	 * Generate YouTube thumbnail URL using direct image URLs (no API required)
	 * Using hqdefault.jpg (480x360) - good size for editing with resize capability
	 */
	getThumbnailUrl(videoId: string): string {
		return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
	},

	/**
	 * Create video info object from YouTube URL
	 */
	getVideoInfo(url: string): IYouTubeVideoInfo | null {
		const videoId = this.extractVideoId(url);
		if (!videoId) return null;

		return {
			videoId,
			thumbnailUrl: this.getThumbnailUrl(videoId),
			originalUrl: url,
		};
	},

	/**
	 * Generate HTML for YouTube thumbnail with helper text
	 * Creates a linked image that opens YouTube video in new tab
	 */
	generateThumbnailHtml(
		videoInfo: IYouTubeVideoInfo,
		altText?: string,
		editorType: 'email' | 'blog' = 'email'
	): string {
		// Validate video ID to prevent injection
		if (!this.isValidVideoId(videoInfo.videoId)) {
			throw new Error('Invalid YouTube video ID');
		}

		const cleanUrl = this.getCleanYouTubeUrl(videoInfo.videoId);
		const escapedAlt = escapeHtml(altText || 'Play YouTube video');
		const escapedVideoId = escapeHtml(videoInfo.videoId);
		const escapedThumbnailUrl = escapeHtml(videoInfo.thumbnailUrl);

		const textElement =
			editorType === 'blog'
				? `<div style="font-size: 12px; color: #666; font-style: italic;">Click the above image to play video</div>`
				: `<span style="font-size: 12px; color: #666; font-style: italic;">Click the above image to play video</span>`;

		return `<div style="display: inline-block;">
			<a href="${escapeHtml(cleanUrl)}" target="_blank" rel="noopener" style="display: block; text-decoration: none;">
				<img src="${escapedThumbnailUrl}" 
					 width="480" 
					 height="360" 
					 alt="${escapedAlt}" 
					 style="display: block; border: 0; max-width: 100%; height: auto;" 
					 data-youtube-id="${escapedVideoId}" />
			</a>
			${textElement}
		</div><br /><br />`;
	},

	/**
	 * Get clean YouTube watch URL from video ID
	 */
	getCleanYouTubeUrl(videoId: string): string {
		return `https://www.youtube.com/watch?v=${videoId}`;
	},

	/**
	 * Convert YouTube URL in text content to thumbnail HTML
	 * Used for paste processing
	 */
	convertUrlsInContent(content: string): string {
		if (!content || typeof content !== 'string') return content;

		// Use a more restrictive pattern that matches complete URLs
		const youtubeUrlPattern =
			/(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|m\.youtube\.com\/watch\?v=)[a-zA-Z0-9_-]{11}(?:\S*)?)/gi;

		return content.replace(youtubeUrlPattern, (match: string) => {
			try {
				const videoInfo = this.getVideoInfo(match);
				if (videoInfo) {
					return this.generateThumbnailHtml(videoInfo);
				}
			} catch {
				// If there's any error processing the URL, return the original match
				// Note: This is expected for malformed URLs and should not interrupt the user flow
			}
			return match;
		});
	},
};
